import {subscribeToMidiEvents} from "../eventBus/midi/midiEventBus";
import {MidiEventTypes} from "../eventBus/midi/MidiEventTypes";
import Synthesizer from "../synthesizer/Synthesizer";
import {NotesEventTypes} from "../eventBus/notes/NotesEventTypes";
import {emitPlayerNote_pressed, emitPlayerNote_released, subscribeToNotesEvents} from "../eventBus/notes/notesEventBus";
import {getAudioContext} from "../audioContext";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import nativeBridge from "../nativeBridge";
import deviceInfo from "../deviceInfo/deviceInfo";
import {settingsStore} from "../../redux/settings/settingsStore";
import {MidiInputSource} from "../eventBus/midi/MidiEventTypes";

/**
 * Handles all origins for a player note (midi, qwerty, ui keyboard)
 * Emits events so the ui can respond (e.g. ui keyboard)
 */
class PlayerNotesPlayer{
  synthesizer: Synthesizer;
  isFootPedalPressed = false;
  notesThatShouldBeStoppedWhenFootPedalIsReleased: Set<number> = new Set<number>();
  unsubscribeFuncs: (() => void)[] = [];
  gainNode: GainNode;
  constructor() {
    this.gainNode = getAudioContext().createGain();
    this.gainNode.connect(getAudioContext().destination);
    this.gainNode.gain.value = settingsStore.getPianoVolume() * 2;
    this.synthesizer = new Synthesizer({destination: this.gainNode});
    this.registerEventListeners();

    subscribeToSettingsEvents((m)=>{
      if(m.type === "volumesChanged"){
        this.gainNode.gain.value = m.volumes.piano * 2;
      }
    })
  }
  destroy(){
    this.unsubscribeFuncs.forEach(u => u());
  }

  registerEventListeners(){
    this.unsubscribeFuncs = [
      subscribeToMidiEvents((e) => this.routeMidiEvent(e), `PlayerNotesPlayer`),
      subscribeToNotesEvents((e) => this.routeNotesEvent(e)),
    ];
  }

  routeMidiEvent(e: MidiEventTypes){
    switch(e.type){
      case "midiNotePlayed":
        return this.onNote_played(e.number, e.velocity, e.receivedTime, e.source, e.deviceId.toString(), e.normalizedMidiInput);
      case "midiNoteStopped":
        return this.onNote_stopped(e.number, e.receivedTime, e.source, e.deviceId.toString(), e.normalizedMidiInput);
      case "midiFootPedalPressed":
        return this.onFootPedal_pressed(e.deviceId.toString());
      case "midiFootPedalReleased":
        return this.onFootPedal_released(e.source, e.deviceId.toString());
    }
  }

  routeNotesEvent(e: NotesEventTypes){
    switch(e.type){
      case "uiKeyboardNotePressed":
        return this.onNote_played(e.midiNumber, 100, performance.now(), "uiKeyboard", "virtualKeyboard", null);
      case "uiKeyboardNoteReleased":
        return this.onNote_stopped(e.midiNumber, performance.now(), "uiKeyboard", "virtualKeyboard", null);
    }
  }

  onNote_played(number: number, velocity = 100, receivedTime: number, source: string, deviceId: string, normalizedMidiInput: MidiInputSource | null) {
    emitPlayerNote_pressed(number, velocity, receivedTime, source, deviceId, normalizedMidiInput);
    if (deviceInfo.getIsAndroid()) {
      nativeBridge.sendMessageToNative({type: "midi.note_played", pitch: number, velocity, timestamp: Date.now(), origin: "onScreenKeyboard"});
    } else {
      this.synthesizer.playNote({midi: number, velocity,});
    }
  }

  onNote_stopped(number: number, receivedTime: number, source: string, deviceId: string, normalizedMidiInput: MidiInputSource | null){
    emitPlayerNote_released(number, receivedTime, source, deviceId, normalizedMidiInput);

    //don't immediately release notes if the pedal is pressed.
    if(!this.isFootPedalPressed){
      if (deviceInfo.getIsAndroid()) {
        nativeBridge.sendMessageToNative({type: "midi.note_stopped", pitch: number, timestamp: Date.now(), origin: "onScreenKeyboard"});
      } else {
        this.synthesizer.releaseNote({midi: number});
      }
    }else{
      this.notesThatShouldBeStoppedWhenFootPedalIsReleased.add(number);
    }
  }

  onFootPedal_pressed(_deviceId: string){
    this.isFootPedalPressed = true;
  }

  /**
   * Any notes that have had their release delayed because the foot pedal was pressed should be released now.
   */
  onFootPedal_released(source: string, deviceId: string){
    this.isFootPedalPressed = false;
    const notesToStop = Array.from(this.notesThatShouldBeStoppedWhenFootPedalIsReleased);
    for(let i = 0; i < notesToStop.length; ++i){
      const midi = notesToStop[i];
      this.onNote_stopped(midi, performance.now(), source, deviceId, null);
    }
    this.notesThatShouldBeStoppedWhenFootPedalIsReleased.clear();
  }
}

const playerNotesPlayer = new PlayerNotesPlayer();
export default playerNotesPlayer;